import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { PersonAdd as PersonAddIcon } from '@mui/icons-material';
import { Authorize, GlobalButton, NavigateLinks } from '@/components';
import { GlobalButtonVariant } from '@/components/buttons/GlobalButton';
import { BUSINESS_ROLE_GROUPS } from '@/components/security/AppRoles';
import { childrenLogisticsNetworkPageBreadcrumbs } from '../../config';
import { SearchBar, UsersTable, UserTypesInfo } from './components';
import { useUsersFilter, useUsersPagination } from './hooks';
import { UsersFilter } from './models';

/** Identidad del filtro, para comparar sin depender del orden de las claves. */
const filterKeyOf = (filter: UsersFilter): string =>
  [
    filter.search || '',
    filter.phoneNumber || '',
    filter.userType || '',
    filter.status || '',
  ].join('|');

export const Users = () => {
  const navigate = useNavigate();
  const breadcrumbs = useMemo(
    () => childrenLogisticsNetworkPageBreadcrumbs(navigate),
    [navigate]
  );

  const { filter, setFilter, initialFilter } = useUsersFilter();
  const { currentPage, pageSize, handlePageChange, handlePageSizeChange } =
    useUsersPagination();

  const handleUserSearch = useCallback((filters: UsersFilter) => {
    const next: UsersFilter = {
      search: filters.search || '',
      phoneNumber: filters.phoneNumber || '',
      userType: filters.userType || '',
      status: filters.status,
    };

    // Se compara antes de setear para no refetchear cuando el filtro no
    // cambio: el buscador reemite el mismo valor en cada render.
    setFilter((prev) => (filterKeyOf(prev) === filterKeyOf(next) ? prev : next));
  }, []);

  // Al cambiar el filtro se vuelve a la primera pagina. Va aca y no adentro
  // del updater de `setFilter`: React puede ejecutar ese updater mas de una
  // vez, y tocar el estado de otro hook desde ahi es un efecto colateral en
  // medio del render.
  const filterKey = filterKeyOf(filter);
  useEffect(() => {
    handlePageChange(1);
  }, [filterKey]);

  return (
    <section className='flex flex-col w-full h-full gap-10'>
      <NavigateLinks actualPage='Usuarios' breadcrumbs={breadcrumbs} />
      <Authorize permission={BUSINESS_ROLE_GROUPS.management}>
        <UserTypesInfo />
        <div className='flex justify-end mb-4 gap-3'>
          <GlobalButton
            variant={GlobalButtonVariant.secondaryButton}
            action={() => navigate('invite/external-operator')}
          >
            <PersonAddIcon />
            Invitar Operador Externo
          </GlobalButton>
          <GlobalButton
            variant={GlobalButtonVariant.secondaryButton}
            action={() => navigate('invite/commerce')}
          >
            <PersonAddIcon />
            Invitar Comercio
          </GlobalButton>
          <GlobalButton
            variant={GlobalButtonVariant.primaryButton}
            action={() => navigate('invite/operator')}
          >
            <PersonAddIcon />
            Invitar Operador Soydelivery
          </GlobalButton>
        </div>
      </Authorize>
      <SearchBar
        onSearch={handleUserSearch}
        initialFilter={initialFilter}
        currentFilter={filter}
      />
      <UsersTable
        usersFilter={filter}
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />
    </section>
  );
};

export default Users;
